import React, { useEffect, useRef } from 'react';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import { RouteProp, useNavigationState, useRoute } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import { cameraService } from '../../../services/central-api/cameras';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import Label from './Label';
import moment from 'moment';
import TimeButton from './TimeButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import CustomButton from '../../../components/CustomButton';
import { RootNavigation } from '../../../Routes';

interface Props {
    navigation: RootNavigation;
}

function VideoAnalysisDetails({ navigation }: Props) {
    const { styles, theme } = useStyles(stylesheet);
    const index = useNavigationState(state => state);
    const { user: authenticatedUser } = useAuth();

    const route = useRoute<RouteProp<CamerasParamList, 'VideoAnalysisDetails'>>();
    const videoRef = useRef<HTMLVideoElement>(null);

    const queryResult = useQuery({
        queryKey: ['videoAnalysis', route.params.id],
        queryFn: () => cameraService.getVideoAnalysis(route.params.id),
        enabled: !!authenticatedUser,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    const skipTo = (time: string) => {
        if (videoRef.current) {
            videoRef.current.currentTime = moment.duration(time.split(':').length == 2 ? `00:${time}` : time).asSeconds();
        }
    };

    useEffect(() => {
        refetch();
    }, [route.params.id, data]);

    return (
        <Container>
            <Header
                title={translate('videoAnalysis')}
                goBackAction={() => {
                    if (index.routes.length > 1) {
                        return navigation.goBack();
                    } else {
                        window.location.href = '/cameras/video-analysis';
                    }
                }}
                actions={data && <CustomButton onPress={() => window.open(data.videoUrl)} text={translate('downloadVideo')} />}
            />
            <Card>
                {
                    !data || isLoading ?
                        <ActivityIndicator size='small' color={theme.colors.iconColor} />
                        :
                        <ScrollView>
                            <MyAppText style={[styles.divider, { fontSize: 18, fontWeight: 'bold' }]}>
                                {translate('analysisInfoLabel')}
                            </MyAppText>

                            <View style={styles.dataContainer}>
                                <Label title={translate('status')} value={translate(data.status)} />
                                <Label title={translate('requestAnalysisDate')} value={moment(data.createdAt).format('DD/MM/YYYY')} />
                                <Label title={translate('requestAnalysisHour')} value={moment(data.createdAt).format('hh:mm')} />
                                <Label
                                    title={translate('analysisPeriod')}
                                    value={`${moment(+data.msStart).format('DD/MM/YYYY')} - ${moment(+data.msStart).format('hh:mm')} ${translate('to')} ${moment(+data.msFinish).format('hh:mm')}`}
                                    maxWidth={300}
                                />
                            </View>

                            <View style={styles.dataContainer}>
                                <Label title={translate('request')} value={data.prompt} maxWidth={'100%'} />
                            </View>

                            <MyAppText style={[styles.divider, { fontSize: 18, fontWeight: 'bold' }]}>
                                {translate('cameraInfoLabel')}
                            </MyAppText>

                            <View style={styles.dataContainer}>
                                <TouchableOpacity onPress={() => window.open(`/cameras/cameras/watch/${data.camera.id}`)}>
                                    <Label title={translate('camera')} value={data.camera.title} isLink={true} />
                                </TouchableOpacity>
                                <Label title={translate('address')} value={data.camera.address} />
                                <Label title={translate('cameraType')} value={data.camera.type.toUpperCase()} />
                                <Label title={translate('cameraTitleIdOrSerialNumber')} value={data.camera.serialNo} />
                            </View>

                            <MyAppText style={[styles.divider, { fontSize: 18, fontWeight: 'bold' }]}>
                                {translate('requesterInfoLabel')}
                            </MyAppText>

                            <View style={styles.dataContainer}>
                                <Label title={translate('name')} value={data.user.name} />
                                <Label title={translate('email')} value={data.user.email} maxWidth={300} />
                                <Label title={translate('registration')} value={data.user.registry} />
                                <Label title={translate('phone')} value={data.user.phone} />
                            </View>

                            <MyAppText style={[styles.divider, { fontSize: 18, fontWeight: 'bold' }]}>
                                {translate('analysisResultLabel')}
                            </MyAppText>

                            <View style={{ gap: 15 }}>
                                <ScrollView horizontal contentContainerStyle={{ flexDirection: 'row', columnGap: 10, paddingRight: 10, paddingBottom: 10 }}>
                                    {Array.isArray(data.analysisResult) ? data.analysisResult.map((result, id) => (
                                        <TimeButton key={id} value={result.timestamp} onPress={() => skipTo(result.timestamp)} />
                                    )) : null}
                                </ScrollView>

                                <video ref={videoRef} width='640' height='360' controls>
                                    <source src={data.videoUrl} type='video/mp4' />
                                </video>
                            </View>
                        </ScrollView>
                }
            </Card>
        </Container>
    );
}

const stylesheet = createStyleSheet(theme => ({
    dataContainer: {
        flexDirection: {
            xs: 'column',
            md: 'row'
        },
        gap: 30,
        marginBottom: 30,
        flexWrap: 'wrap'
    },

    divider: {
        width: '100%',
        borderBottomColor: theme.colors.container.border,
        borderBottomWidth: 2,
        marginBottom: 10
    }
}));

export default VideoAnalysisDetails;
